import { useEffect, useState, type ReactNode } from 'react'
import { Box, Flex, Text, Dialog, Portal } from '@chakra-ui/react'
import { ACCENT, FONT_DISPLAY, INK, INK_MUTED, LINE, SHADOW_LG, SUNK } from '../tokens'

interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  subtitle?: string
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  children: ReactNode
  footer?: ReactNode
}

const SIZE_MAP = {
  sm: { maxW: { base: '100%', md: '400px' }, w: { base: '100vw', md: '400px' } },
  md: { maxW: { base: '100%', md: '520px' }, w: { base: '100vw', md: '520px' } },
  lg: { maxW: { base: '100%', md: '95vw', lg: '720px' }, w: { base: '100vw', md: '95vw', lg: '720px' } },
  xl: { maxW: { base: '100%', md: '95vw', lg: '1000px' }, w: { base: '100vw', md: '95vw', lg: '1000px' } },
  full: { maxW: '100%', w: '100vw' },
}

export function Modal({ open, onClose, title, subtitle, size = 'md', children, footer }: ModalProps) {
  const sizeProps = SIZE_MAP[size]

  // El Dialog NUNCA debe montarse ya-abierto: cuando un padre lo monta con open=true
  // (p. ej. remontando con `key` al abrir), Chakra/zag aplica el scroll-lock + aria-hidden
  // en el montaje, pero ese ciclo no se empareja con el teardown y deja `<body>` con
  // `pointer-events:none` y `#root` con `aria-hidden` → página inerte ("congelada").
  // Solución: montar el Dialog CERRADO y abrirlo al frame siguiente; al cerrar, pasar por
  // open=false (camino que sí libera el lock) antes de desmontar.
  const [mounted, setMounted] = useState(false)
  const [active, setActive] = useState(false)

  useEffect(() => {
    if (open) {
      setMounted(true)
      const id = requestAnimationFrame(() => setActive(true))
      return () => cancelAnimationFrame(id)
    }
    setActive(false)
    const t = setTimeout(() => setMounted(false), 250)
    return () => clearTimeout(t)
  }, [open])

  if (!mounted) return null

  return (
    <Dialog.Root open={active} onOpenChange={(e) => { if (!e.open) onClose() }}>
      <Portal>
        <Dialog.Backdrop bg="rgba(42, 30, 20, 0.42)" backdropFilter="blur(4px)" />
        <Dialog.Positioner>
          <Dialog.Content
            bg="white"
            borderRadius={{ base: '0', md: '20px' }}
            boxShadow={SHADOW_LG}
            border={{ base: 'none', md: `1px solid ${LINE}` }}
            overflow="hidden"
            p={0}
            maxH={{ base: '100vh', md: '90vh' }}
            display="flex"
            flexDirection="column"
            {...sizeProps}
          >
            {/* Veta del módulo: el modal pertenece a la sección donde se abrió */}
            <Box h="3px" flexShrink={0} style={{ background: `linear-gradient(90deg, ${ACCENT}, transparent 85%)` }} />
            {/* Header */}
            <Flex px={6} pt={5} pb={4} justifyContent="space-between" alignItems="flex-start" flexShrink={0}
              borderBottom="1px solid" borderColor={LINE}
            >
              <Box>
                <Dialog.Title fontSize="21px" fontWeight="800" color={INK} fontFamily={FONT_DISPLAY} letterSpacing="-0.03em">
                  {title}
                </Dialog.Title>
                {subtitle && (
                  <Text fontSize="sm" color={INK_MUTED} mt={1}>{subtitle}</Text>
                )}
              </Box>
              <Dialog.CloseTrigger asChild>
                <Box
                  as="button" {...{ type: 'button', 'aria-label': 'Cerrar' }} p={1.5} borderRadius="10px"
                  color={INK_MUTED} cursor="pointer" _hover={{ bg: SUNK, color: INK, transform: 'rotate(90deg)' }} transition="all .2s"
                  onClick={onClose}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </Box>
              </Dialog.CloseTrigger>
            </Flex>

            {/* Body */}
            <Box px={6} py={5} flex={1} overflowY="auto">
              {children}
            </Box>

            {/* Footer */}
            {footer && (
              <Flex px={6} py={4} justifyContent="flex-end" gap={3} flexShrink={0}
                borderTop="1px solid" borderColor={LINE} bg="#faf8f3"
              >
                {footer}
              </Flex>
            )}
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
